import {
  useFormContext,
  type FieldPath,
  type FieldValues,
} from "react-hook-form"

import {
  ChipGroup,
  type ChipGroupVariant,
  type ChipOption,
} from "@/components/form-elements/ChipGroup"
import { FieldInfo } from "@/components/ui/field-info"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { cn } from "@/lib/utils"

export type FormChipGroupProps<
  T extends FieldValues = FieldValues,
  V extends string | number | boolean = string,
> = {
  name: FieldPath<T>
  label?: string
  // Explanation the label itself is too long to carry, shown behind an "i".
  info?: string
  options: ChipOption<V>[]
  className?: string
  disabled?: boolean
  onChange?: (value: V) => void
  variant?: ChipGroupVariant
}

export function FormChipGroup<
  T extends FieldValues = FieldValues,
  V extends string | number | boolean = string,
>({
  name,
  label,
  info,
  options,
  className,
  disabled,
  onChange,
  variant,
}: FormChipGroupProps<T, V>) {
  const form = useFormContext<T>()

  return (
    <FormField
      control={form.control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem className={cn("w-full", className)}>
          {label && (
            <FormLabel>
              {label}
              {info && <FieldInfo text={info} />}
            </FormLabel>
          )}
          <FormControl>
            <ChipGroup
              name={label ?? name}
              options={options}
              variant={variant}
              value={field.value as V}
              onChange={(value) => {
                field.onChange(value)
                onChange?.(value)
              }}
              disabled={disabled || field.disabled}
            />
          </FormControl>
          <FormMessage className="form-error" />
        </FormItem>
      )}
    />
  )
}
